/**
 * The client half of `/py-api/v1/privacy`.
 *
 * Own-scoped by construction: none of these take a user id, because the server
 * takes it from the session and there is no parameter that could point them at
 * somebody else's row.
 *
 * The endpoints are reached through the `preferences`-style path on the
 * `admin`-free FastAPI base. There is no entry in `API_DOMAINS` for privacy
 * because a domain there is a ROOT under `/py-api/v1`, and privacy is exactly
 * that root — `domainBaseUrl` would add nothing a constant does not. The base
 * prefix is imported rather than written out so the one place it could ever
 * change stays one place.
 */
import { apiFetch } from "@/lib/api/client";
import { API_HOST, BACKEND_PREFIX } from "@/lib/api/config";
import { normalizePrivacy, type PrivacySettings } from "./store";

const BASE = `${API_HOST}${BACKEND_PREFIX.fastapi}/privacy`;

/** A short-lived proof that the user re-authenticated. See `reveal.tsx`. */
export interface RevealGrant {
  token: string;
  /** Seconds the grant is good for, straight from the user's own preference. */
  expiresIn: number;
  /** ISO-8601, server clock. */
  expiresAt: string;
}

export class RevealRejected extends Error {
  constructor(message = "That password did not match.") {
    super(message);
    this.name = "RevealRejected";
  }
}

/** This user's stored settings. Falls back to defaults rather than throwing —
 * a settings fetch that fails must not stop the app from painting. */
export async function fetchPrivacy(): Promise<PrivacySettings> {
  const res = await apiFetch(BASE);
  if (!res.ok) throw new Error(`privacy fetch failed (${res.status})`);
  return normalizePrivacy(await res.json());
}

/** Full replace — the settings screen holds all four switches. */
export async function savePrivacy(settings: PrivacySettings): Promise<PrivacySettings> {
  const res = await apiFetch(BASE, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error(`privacy save failed (${res.status})`);
  return normalizePrivacy(await res.json());
}

/**
 * Re-authenticate by password and get a grant.
 *
 * A 401 becomes `RevealRejected` rather than a generic error so the dialog can
 * say "wrong password" without string-matching a message. Every other failure
 * stays an `Error` — a network problem is not a wrong password, and telling
 * the user it was would send them hunting for a typo that is not there.
 */
export async function requestRevealGrant(password: string): Promise<RevealGrant> {
  const res = await apiFetch(`${BASE}/reveal`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (res.status === 401) throw new RevealRejected();
  if (!res.ok) throw new Error(`reveal failed (${res.status})`);
  return (await res.json()) as RevealGrant;
}
